import type { SnakeCasedProperties } from 'type-fest';
import camelcaseKeys from 'utils/camelCaseKeys';
import { catchNetworkExceptions } from 'utils/exception';
import { mapNetworkStatuses } from 'utils/exception/mapNetworkStatuses';

import {
  Assigned,
  BookableAsOptions,
  ServiceInfoType,
} from 'components/Apps/PriceBook/Services/Booking/useBookingData';
import { Codeowners } from 'components/Common/Error/Codeowners';
import RestAPI from 'components/Common/restApi';

import { AvailableServicesResponse, BookingData, EarliestAvailibilityResponse } from '../types/types';

export interface SetAvailableServiceProps {
  uuid: string;
  enabled: boolean;
  bookableAs: BookableAsOptions;
  assigned: Assigned;
  question_1: string;
  question_2: string;
  duration: number;
  useBookingDuration: boolean;
}

export const fetchAvailableServices = () => {
  return catchNetworkExceptions(
    () =>
      RestAPI.get<AvailableServicesResponse>('/alpha/online_booking/settings/available_services').then((response) => ({
        ...response,
        ...(response?.data ? { data: camelcaseKeys(response.data) } : {}),
      })),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const setAvailableService = ({
  uuid,
  enabled,
  bookableAs,
  assigned,
  question_1,
  question_2,
  duration,
  useBookingDuration,
}: SetAvailableServiceProps) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.updateWithFormData(`/alpha/online_booking/settings/available_services/${uuid}`, {
        enabled,
        bookable_as: bookableAs,
        assigned,
        question_1,
        question_2,
        duration,
        use_booking_duration: useBookingDuration,
      }),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const getServiceInfo = (uuid: string) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.get<SnakeCasedProperties<ServiceInfoType>>(`/alpha/online_booking/settings/available_services/${uuid}`),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const fetchBookingHours = () => {
  return catchNetworkExceptions(
    () => RestAPI.get<BookingData>('/alpha/online_booking/settings/booking_hours'),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const updateBookingHours = (bookingHours: BookingData) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.updateWithFormData<BookingData, BookingData>(
        '/alpha/online_booking/settings/booking_hours',
        bookingHours
      ),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const fetchEarliestAvailability = () => {
  return catchNetworkExceptions(
    () => RestAPI.get<EarliestAvailibilityResponse>('/alpha/online_booking/settings/earliest_availability'),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const updateEarliestAvailability = (daysOut: number) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.updateWithFormData('/alpha/online_booking/settings/earliest_availability', {
        days_out: daysOut,
      }),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};

export const updateArrivalWindow = (arrivalWindow: number) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.updateWithFormData('/alpha/organization/default_arrival_window', {
        arrival_window_minutes: arrivalWindow,
      }),
    Codeowners.CUSTOMER_INTAKE,
    mapNetworkStatuses
  );
};
